import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { useRepProfile } from '@/lib/RepProfileContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Users, Plus, SlidersHorizontal, X,
  ChevronLeft, ChevronRight, AlertCircle, Loader2
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import CustomerCard from '@/components/customers/CustomerCard';
import EmptyState from '@/components/shared/EmptyState';
import { api } from '@/api/apiClient';

const PAGE_SIZE = 50;

const FILTER_CHIPS = [
  { key: 'all',     label: 'الكل' },
  { key: 'overdue', label: '🔴 متأخر السداد' },
  { key: 'vip',     label: '⭐ VIP' },
];

const SORT_OPTIONS = [
  { key: 'name_asc',    label: 'الاسم أ-ي' },
  { key: 'debit_desc',  label: 'الأعلى مديونية' },
  { key: 'credit_desc', label: 'الأعلى رصيد' },
];

// Skeleton card loader
function SkeletonCard() {
  return (
    <div className="bg-card border border-border rounded-2xl p-4 flex gap-3 animate-pulse">
      <div className="w-12 h-12 bg-muted rounded-2xl flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 bg-muted rounded w-3/4" />
        <div className="h-3 bg-muted rounded w-1/2" />
        <div className="h-3 bg-muted rounded w-2/5" />
      </div>
    </div>
  );
}

export default function Customers() {
  const navigate = useNavigate();
  const { odooSession } = useOdooAuth();
  const { profile: repProfile, isManager } = useRepProfile();
  // Managers always can; reps follow their profile flag.
  const canAddCustomer = isManager || !!repProfile.allow_add_customer;
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const [showSort, setShowSort] = useState(false);
  const searchTimerRef = useRef(null);
  const listRef = useRef(null);

  const fetchPage = useCallback(async (pg, q, flt, srt, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      const qq = q?.trim();
      const params = {
        search: qq || undefined,
        limit: PAGE_SIZE,
        page: pg,
        filter: flt !== 'all' ? flt : undefined,
        sort_by: srt };
      // Always use the rep-scoped endpoint '/sellgo_v5_customer_search' (the
      // same one orders / gifts / visits use) so a rep sees ONLY their own
      // customers — not the whole company. It handles both an empty query
      // (scoped browse) and a typed search. Falls back to the legacy
      // '/customers' only if the scoped endpoint is unavailable.
      let res;
      try {
        res = await api.get('/sellgo_v5_customer_search', params);
      } catch {
        res = await api.get('/customers', params);
      }
      if (res?.success) {
        const newData = res.data || [];
        setCustomers(prev => append ? [...prev, ...newData] : newData);
        setTotal(res.total || 0);
      } else {
        throw new Error(res?.error || 'فشل التحميل');
      }
    } catch (e) {
      setError(e.message);
    }
    if (append) setLoadingMore(false);
    else setLoading(false);
  }, []);

  // Initial load
  useEffect(() => {
    fetchPage(0, '', 'all', 'name_asc');
  }, []);

  const handleSearchChange = (val) => {
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(0);
      fetchPage(0, val, activeFilter, sortBy);
    }, 350);
  };

  const handleFilterChange = (key) => {
    setActiveFilter(key);
    setPage(0);
    fetchPage(0, search, key, sortBy);
  };

  const handleSortChange = (key) => {
    setSortBy(key);
    setShowSort(false);
    setPage(0);
    fetchPage(0, search, activeFilter, key);
  };

  const loadNextPage = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPage(nextPage, search, activeFilter, sortBy, true);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const hasMore = page + 1 < totalPages;

  return (
    <div className="flex flex-col h-full" dir="rtl">
      {/* Sticky Header + Search */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border">
        {/* Title bar */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-foreground">العملاء</h1>
            <span className="text-xs font-semibold bg-primary/10 text-primary px-2.5 py-1 rounded-full">
              {total.toLocaleString('ar-EG')}
            </span>
          </div>
          {canAddCustomer && (
            <Link to="/customers/new">
              <Button size="sm" className="bg-primary gap-1.5 rounded-xl shadow-lg shadow-primary/30">
                <Plus className="w-4 h-4" />
                عميل جديد
              </Button>
            </Link>
          )}
        </div>

        {/* Search bar */}
        <div className="px-4 pb-2">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder="بحث بالاسم، الهاتف، الكود، أو المدينة..."
              className="pr-10 bg-card rounded-xl border-border"
            />
            {search && (
              <button onClick={() => handleSearchChange('')} className="absolute left-3 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>

        {/* Filter chips + Sort */}
        <div className="flex items-center gap-2 px-4 pb-3 overflow-x-auto no-scrollbar">
          {FILTER_CHIPS.map(chip => (
            <button
              key={chip.key}
              onClick={() => handleFilterChange(chip.key)}
              className={`flex-shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${
                activeFilter === chip.key
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-card border border-border text-muted-foreground hover:border-primary/40'
              }`}
            >
              {chip.label}
            </button>
          ))}
          <div className="relative flex-shrink-0 mr-auto">
            <button
              onClick={() => setShowSort(s => !s)}
              className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-card border border-border text-muted-foreground hover:border-primary/40"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              {SORT_OPTIONS.find(s => s.key === sortBy)?.label}
            </button>
            <AnimatePresence>
              {showSort && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="absolute left-0 top-full mt-1 bg-card border border-border rounded-xl shadow-xl z-30 min-w-[160px] overflow-hidden"
                >
                  {SORT_OPTIONS.map(opt => (
                    <button
                      key={opt.key}
                      onClick={() => handleSortChange(opt.key)}
                      className={`w-full text-right px-4 py-2.5 text-sm transition-colors ${
                        sortBy === opt.key ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-muted text-foreground'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Customer List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2 pb-6" ref={listRef}>
        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center">
              <AlertCircle className="w-7 h-7 text-red-500" />
            </div>
            <p className="font-semibold text-foreground">فشل التحميل</p>
            <p className="text-sm text-muted-foreground text-center max-w-xs">{error}</p>
            <Button onClick={() => fetchPage(0, search, activeFilter, sortBy)} variant="outline" size="sm">
              إعادة المحاولة
            </Button>
          </div>
        ) : customers.length === 0 ? (
          <EmptyState
            icon={Users}
            title="لا يوجد عملاء"
            description={search ? `لم يتم العثور على نتائج لـ "${search}"` : 'لا يوجد عملاء بهذه المعايير'}
            action={search || activeFilter !== 'all' ? (
              <Button variant="outline" size="sm" onClick={() => { setSearch(''); handleFilterChange('all'); }}>
                مسح الفلاتر
              </Button>
            ) : null}
          />
        ) : (
          <>
            <AnimatePresence mode="popLayout">
              {customers.map((customer, i) => (
                <motion.div
                  key={customer.id}
                  layout
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.015, 0.3) }}
                >
                  <CustomerCard customer={customer} />
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Load more / pagination */}
            {hasMore && (
              <div className="pt-2 pb-4">
                <Button
                  onClick={loadNextPage}
                  variant="outline"
                  className="w-full rounded-xl"
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <><Loader2 className="w-4 h-4 animate-spin ml-2" /> جاري التحميل...</>
                  ) : (
                    `تحميل المزيد (${total - customers.length} متبقي)`
                  )}
                </Button>
              </div>
            )}

            <p className="text-center text-xs text-muted-foreground pb-4">
              {customers.length.toLocaleString('ar-EG')} من {total.toLocaleString('ar-EG')} عميل
            </p>
          </>
        )}
      </div>
    </div>
  );
}